/* Nuro v12: service reservation only. Never subscribes a marketing channel.
 * API 202 = request accepted for processing, not allocated place or delivered mail.
 * Preview is local. Root binds mode from theme.role before publication.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.NuroReservation = api;
})(typeof window !== 'undefined' ? window : this, function () {
  'use strict';
  var COMPANY = 'ULpQch';
  var REVISION = '2026-07-15';
  var VERSION = '2026-09-10 v12';
  var pending = new Map();
  var accepted = new Set();

  function sourceName(value) {
    if (value === 'PDP notify' || value === 'pdp') return 'pdp';
    if (value === 'Founding 100 side' || value === 'founding') return 'founding';
    return 'reservation';
  }

  function normalizeEmail(value) {
    var email = String(value || '').trim();
    if (email.length > 254 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      var error = new Error('invalid-email');
      error.code = 'invalid-email';
      throw error;
    }
    // Preserve the local part; no guesses about provider-specific alias rules.
    return email.replace(/@([^@]+)$/, function (_, domain) { return '@' + domain.toLowerCase(); });
  }

  function payloads(input) {
    return { reservation: { data: { type: 'profile', attributes: {
      email: normalizeEmail(input.email),
      properties: {
        founding_reservation: true,
        founding_reservation_last_requested_at: input.timestamp,
        founding_reservation_source: sourceName(input.source),
        founding_reservation_version: VERSION,
        founding_reservation_purpose: 'Besked om reservationen og første batch'
      }
    } } }, marketing: null };
  }

  function failure(code, status) {
    var error = new Error(code);
    error.code = code;
    if (status) error.status = status;
    return error;
  }

  function post(body, fetcher, timeoutMs) {
    return new Promise(function (resolve, reject) {
      var done = false;
      var controller = typeof AbortController === 'function' ? new AbortController() : null;
      var timer = setTimeout(function () {
        finish(failure('uncertain'));
        if (controller) controller.abort();
      }, timeoutMs);
      function finish(error) {
        if (done) return;
        done = true;
        clearTimeout(timer);
        if (error) reject(error); else resolve();
      }
      var options = {
        method: 'POST',
        headers: { 'Content-Type': 'application/vnd.api+json', accept: 'application/vnd.api+json', revision: REVISION },
        body: JSON.stringify(body)
      };
      if (controller) options.signal = controller.signal;
      Promise.resolve().then(function () {
        return fetcher('https://a.klaviyo.com/client/profiles/?company_id=' + COMPANY, options);
      }).then(function (response) {
        if (response.status === 202) finish();
        else if (response.status === 429) finish(failure('busy', 429));
        else if (response.status >= 400 && response.status < 500 && response.status !== 408) finish(failure('rejected', response.status));
        else finish(failure('uncertain', response.status));
      }).catch(function () { finish(failure('uncertain')); });
    });
  }

  async function submit(input, transport) {
    var data = payloads(input);
    if (input.mode !== 'live') return { preview: true, reservationAccepted: false, duplicate: false, marketingAccepted: false };
    var email = data.reservation.data.attributes.email;
    if (accepted.has(email)) return { preview: false, reservationAccepted: true, duplicate: true, marketingAccepted: false };
    if (pending.has(email)) {
      await pending.get(email);
      return { preview: false, reservationAccepted: true, duplicate: true, marketingAccepted: false };
    }
    var fetcher = transport || (typeof fetch === 'function' && fetch);
    if (!fetcher) throw failure('unavailable');
    var timeoutMs = typeof input.timeoutMs === 'number' ? Math.max(1, Math.min(input.timeoutMs, 30000)) : 15000;
    var operation = post(data.reservation, fetcher, timeoutMs);
    pending.set(email, operation);
    try {
      await operation;
      accepted.add(email);
      return { preview: false, reservationAccepted: true, duplicate: false, marketingAccepted: false };
    } finally {
      pending.delete(email);
    }
  }

  function bind(options) {
    var form = document.getElementById(options.formId);
    if (!form || form.dataset.nuroReservationBound === 'true') return false;
    var button = form.querySelector('button[type="submit"]') || form.querySelector('button');
    if (button) button.disabled = true;
    var email = document.getElementById(options.emailId);
    var message = document.getElementById(options.messageId);
    var mode = options.mode === 'live' ? 'live' : 'preview';
    if (!email || !message || !button || (mode === 'live' && typeof fetch !== 'function' && !options.transport)) return false;
    var inFlight = false;
    message.setAttribute('role', 'status');
    message.setAttribute('aria-live', 'polite');
    message.setAttribute('tabindex', '-1');
    email.setAttribute('autocomplete', 'email');
    email.setAttribute('inputmode', 'email');

    function show(text, state, focus) {
      message.hidden = false;
      message.style.display = 'block';
      message.style.color = state === 'error' ? '#a12622' : '#215d3a';
      message.dataset.state = state;
      message.textContent = text;
      if (focus && typeof message.focus === 'function') message.focus();
    }

    form.addEventListener('submit', async function (event) {
      event.preventDefault();
      if (inFlight || !form.reportValidity()) return;
      inFlight = true;
      button.disabled = true;
      form.setAttribute('aria-busy', 'true');
      show(mode === 'preview' ? 'Kontrollerer formularen.' : 'Sender din anmodning.', 'pending', false);
      var outcome;
      try {
        outcome = await submit({ email: email.value, timestamp: new Date().toISOString(), source: options.source, mode: mode }, options.transport);
      } catch (error) {
        var text = 'Nuro kunne ikke bekræfte, at anmodningen blev modtaget. Prøv igen, eller skriv til info@nuro.nu.';
        if (error.code === 'invalid-email') text = 'Skriv en gyldig e-mailadresse.';
        if (error.code === 'busy') text = 'Der er travlt lige nu. Vent lidt, og prøv igen, eller skriv til info@nuro.nu.';
        if (error.code === 'rejected') text = 'Anmodningen blev ikke accepteret. Kontrollér e-mailadressen, eller skriv til info@nuro.nu.';
        show(text, 'error', true);
      } finally {
        inFlight = false;
        button.disabled = false;
        form.removeAttribute('aria-busy');
      }
      if (!outcome) return;
      if (outcome.preview) {
        show('Testtilstand: Formularen er kontrolleret. Der er ikke sendt en reservation eller en e-mail.', 'preview', true);
        return;
      }
      form.reset();
      show('Tak. Din anmodning er sendt til Nuro. Du får besked, når første batch er klar, og om du kan benytte Founding-tilbuddet.', 'accepted', true);
      // Ancillary measurement must never turn successful capture into a false failure.
      if (!outcome.duplicate) {
        try {
          var privacy = window.Shopify && window.Shopify.customerPrivacy;
          if (privacy && privacy.analyticsProcessingAllowed() && window.Shopify.analytics && window.Shopify.analytics.publish) {
            var publication = window.Shopify.analytics.publish('nuro_reservation_request_accepted', { source: sourceName(options.source) });
            if (publication && typeof publication.catch === 'function') publication.catch(function () {});
          }
        } catch (ignored) {}
        try {
          form.dispatchEvent(new CustomEvent('nuro:reservation-accepted', { bubbles: true, detail: { source: sourceName(options.source), state: 'request_accepted' } }));
        } catch (ignored) {}
      }
    });
    form.dataset.nuroReservationBound = 'true';
    button.disabled = false;
    return true;
  }

  return { payloads: payloads, submit: submit, bind: bind, normalizeEmail: normalizeEmail, revision: REVISION, version: VERSION };
});
